import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Select, { type SelectProps } from "@cloudscape-design/components/select";
import Checkbox from "@cloudscape-design/components/checkbox";
import DateRangePicker, { type DateRangePickerProps } from "@cloudscape-design/components/date-range-picker";
import Flashbar, { type FlashbarProps } from "@cloudscape-design/components/flashbar";
import Textarea from "@cloudscape-design/components/textarea";
import { fetchSimtTickets } from "../api";
import type { Ticket } from "../types";

// ─── Period options ───

type PeriodType = "weekly" | "biweekly" | "monthly" | "quarterly" | "annually" | "custom";

const PERIOD_OPTIONS: SelectProps.Option[] = [
  { value: "weekly", label: "Weekly (7 days)" },
  { value: "biweekly", label: "Biweekly (14 days)" },
  { value: "monthly", label: "Monthly (30 days)" },
  { value: "quarterly", label: "Quarterly (90 days)" },
  { value: "annually", label: "Annually (365 days)" },
  { value: "custom", label: "Custom range" },
];

const PERIOD_DAYS: Record<Exclude<PeriodType, "custom">, number> = {
  weekly: 7,
  biweekly: 14,
  monthly: 30,
  quarterly: 90,
  annually: 365,
};

// ─── Incident type detection ───

function detectIncidentType(ticket: Ticket): string {
  const desc = (ticket.description || "").toLowerCase();
  const tags = (ticket.tags || []).map((t) => t.toLowerCase());

  if (tags.includes("ai/ml") || desc.includes("genai") || desc.includes("ai tool") || desc.includes("machine learning")) {
    return "AI/GenAI tool related";
  }
  if (desc.includes("social engineering") || desc.includes("insider threat")) {
    return "Social engineering / insider threat";
  }
  if (desc.includes("vendor") || desc.includes("third party") || desc.includes("3p") || desc.includes("contractor")) {
    return "3P vendor";
  }
  if (desc.includes("non-prod") || desc.includes("integ") || desc.includes("test environment")) {
    return "Real PII in non-prod";
  }
  if (desc.includes("publicly accessible") || desc.includes("external exposure") || desc.includes("public internet")) {
    return "External exposure confirmed";
  }
  if (desc.includes("access control") || desc.includes("authorization") || desc.includes("permissions") || desc.includes("unauthorized")) {
    return "Authorization/access control bugs";
  }
  if (desc.includes("email") || desc.includes("sent to wrong") || desc.includes("cc") || desc.includes("bcc") || desc.includes("misdirect")) {
    return "Human error (misdirected comms)";
  }
  if (desc.includes("policy") && (desc.includes("non-compliance") || desc.includes("without contractual"))) {
    return "Policy non-compliance (contractual)";
  }

  return "Other";
}

// ─── Report calculation ───

interface PeriodMetrics {
  label: string;
  newIntake: number;
  resolved: number;
  closureRate: string;
  blockedOnExternal: number;
  firstContactSla: string;
  avgFcTimeSev2: string;
  incidentTypes: Record<string, number>;
}

function calculatePeriodMetrics(
  tickets: Ticket[],
  startDate: Date,
  endDate: Date,
  label: string
): PeriodMetrics {
  const inPeriod = tickets.filter((t) => {
    const created = new Date(t.createdAt);
    return created >= startDate && created <= endDate;
  });

  const resolved = tickets.filter((t) => {
    const updated = new Date(t.lastUpdatedAt);
    return (t.status === "Resolved" || t.status === "Closed") && updated >= startDate && updated <= endDate;
  });

  const blocked = tickets.filter((t) => t.waitingOn !== "none" && t.status !== "Resolved" && t.status !== "Closed");

  // Incident type categorization
  const incidentTypes: Record<string, number> = {
    "Authorization/access control bugs": 0,
    "Human error (misdirected comms)": 0,
    "AI/GenAI tool related": 0,
    "Real PII in non-prod": 0,
    "3P vendor": 0,
    "External exposure confirmed": 0,
    "Social engineering / insider threat": 0,
    "Policy non-compliance (contractual)": 0,
  };

  for (const t of inPeriod) {
    const type = detectIncidentType(t);
    if (type in incidentTypes) {
      incidentTypes[type]++;
    }
  }

  const newCount = inPeriod.length;
  const resolvedCount = resolved.length;
  const closureRate = newCount > 0 ? `${Math.round((resolvedCount / newCount) * 100)}%` : "N/A";

  // First contact SLA (simplified: assume all tickets get first contact within SLA for mock)
  const firstContactSla = "100%";
  const avgFcTimeSev2 = "< 4 hrs";

  return {
    label,
    newIntake: newCount,
    resolved: resolvedCount,
    closureRate,
    blockedOnExternal: blocked.length,
    firstContactSla,
    avgFcTimeSev2,
    incidentTypes,
  };
}

// ─── Report formatting ───

function formatReportMarkdown(current: PeriodMetrics, previous?: PeriodMetrics): string {
  const header = previous
    ? `| Metric | ${previous.label} | ${current.label} |`
    : `| Metric | ${current.label} |`;
  const divider = previous
    ? `|--------|${"-".repeat(previous.label.length + 2)}|${"-".repeat(current.label.length + 2)}|`
    : `|--------|${"-".repeat(current.label.length + 2)}|`;

  const row = (metric: string, cur: string | number, prev?: string | number) =>
    previous
      ? `| ${metric} | ${prev} | ${cur} |`
      : `| ${metric} | ${cur} |`;

  let report = `## Volume & Throughput\n\n`;
  report += `${header}\n${divider}\n`;
  report += row("New intake (SIM-T + Taskei)", current.newIntake, previous?.newIntake) + "\n";
  report += row("Resolved/closed", current.resolved, previous?.resolved) + "\n";
  report += row("Closure rate", current.closureRate, previous?.closureRate) + "\n";
  report += row("Blocked on external", current.blockedOnExternal, previous?.blockedOnExternal) + "\n";
  report += row("First contact SLA", current.firstContactSla, previous?.firstContactSla) + "\n";
  report += row("Avg FC time (Sev-2)", current.avgFcTimeSev2, previous?.avgFcTimeSev2) + "\n";

  report += `\n## Incident Type Shifts\n\n`;

  const typeHeader = previous
    ? `| Category | ${previous.label} | ${current.label} |`
    : `| Category | ${current.label} |`;
  const typeDivider = previous
    ? `|----------|${"-".repeat(previous.label.length + 2)}|${"-".repeat(current.label.length + 2)}|`
    : `|----------|${"-".repeat(current.label.length + 2)}|`;

  report += `${typeHeader}\n${typeDivider}\n`;

  const categories = Object.keys(current.incidentTypes);
  for (const cat of categories) {
    report += row(cat, current.incidentTypes[cat], previous?.incidentTypes[cat]) + "\n";
  }

  return report;
}

function formatReportCsv(current: PeriodMetrics, previous?: PeriodMetrics): string {
  let csv = previous
    ? `Metric,${previous.label},${current.label}\n`
    : `Metric,${current.label}\n`;

  const row = (metric: string, cur: string | number, prev?: string | number) =>
    previous ? `${metric},${prev},${cur}` : `${metric},${cur}`;

  csv += row("New intake", current.newIntake, previous?.newIntake) + "\n";
  csv += row("Resolved/closed", current.resolved, previous?.resolved) + "\n";
  csv += row("Closure rate", current.closureRate, previous?.closureRate) + "\n";
  csv += row("Blocked on external", current.blockedOnExternal, previous?.blockedOnExternal) + "\n";
  csv += row("First contact SLA", current.firstContactSla, previous?.firstContactSla) + "\n";
  csv += row("Avg FC time Sev-2", current.avgFcTimeSev2, previous?.avgFcTimeSev2) + "\n";
  csv += "\n";

  csv += previous
    ? `Category,${previous.label},${current.label}\n`
    : `Category,${current.label}\n`;

  for (const [cat, count] of Object.entries(current.incidentTypes)) {
    csv += row(cat, count, previous?.incidentTypes[cat]) + "\n";
  }

  return csv;
}

// ─── Component ───

export default function ReportsPage() {
  const [period, setPeriod] = useState<PeriodType>("biweekly");
  const [comparePrevious, setComparePrevious] = useState(true);
  const [reportText, setReportText] = useState("");
  const [reportGenerated, setReportGenerated] = useState(false);
  const [notifications, setNotifications] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);

  const { data: tickets = [] } = useQuery({
    queryKey: ["tickets"],
    queryFn: fetchSimtTickets,
  });

  function notify(type: "success" | "error", content: string) {
    setNotifications((prev) => [
      ...prev,
      { type, content, dismissible: true, id: String(Date.now()) },
    ]);
  }

  function handleGenerateReport() {
    const now = new Date();
    let periodDays: number;
    let endDate = now;
    let startDate: Date;

    if (period === "custom" && dateRange && dateRange.type === "absolute") {
      startDate = new Date(dateRange.startDate);
      endDate = new Date(dateRange.endDate);
      periodDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    } else {
      periodDays = PERIOD_DAYS[period as Exclude<PeriodType, "custom">] || 14;
      startDate = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000);
    }

    const formatDate = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

    const currentLabel = `${formatDate(startDate)} - ${formatDate(endDate)}`;
    const currentMetrics = calculatePeriodMetrics(tickets, startDate, endDate, currentLabel);

    let previousMetrics: PeriodMetrics | undefined;
    if (comparePrevious) {
      const prevEnd = new Date(startDate.getTime() - 1);
      const prevStart = new Date(prevEnd.getTime() - periodDays * 24 * 60 * 60 * 1000);
      const prevLabel = `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
      previousMetrics = calculatePeriodMetrics(tickets, prevStart, prevEnd, prevLabel);
    }

    const report = formatReportMarkdown(currentMetrics, previousMetrics);
    setReportText(report);
    setReportGenerated(true);
  }

  function handleCopyToClipboard() {
    navigator.clipboard.writeText(reportText).then(
      () => notify("success", "Report copied to clipboard"),
      () => notify("error", "Failed to copy")
    );
  }

  function handleExportCsv() {
    const now = new Date();
    let periodDays: number;
    let endDate = now;
    let startDate: Date;

    if (period === "custom" && dateRange && dateRange.type === "absolute") {
      startDate = new Date(dateRange.startDate);
      endDate = new Date(dateRange.endDate);
      periodDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    } else {
      periodDays = PERIOD_DAYS[period as Exclude<PeriodType, "custom">] || 14;
      startDate = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000);
    }

    const formatDate = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

    const currentLabel = `${formatDate(startDate)} - ${formatDate(endDate)}`;
    const currentMetrics = calculatePeriodMetrics(tickets, startDate, endDate, currentLabel);

    let previousMetrics: PeriodMetrics | undefined;
    if (comparePrevious) {
      const prevEnd = new Date(startDate.getTime() - 1);
      const prevStart = new Date(prevEnd.getTime() - periodDays * 24 * 60 * 60 * 1000);
      const prevLabel = `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
      previousMetrics = calculatePeriodMetrics(tickets, prevStart, prevEnd, prevLabel);
    }

    const csv = formatReportCsv(currentMetrics, previousMetrics);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pirt-metrics-${startDate.toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    notify("success", "CSV exported");
  }

  return (
    <SpaceBetween size="l">
      <Flashbar items={notifications} />

      <Header variant="h1">Reports</Header>

      {/* Controls */}
      <Container header={<Header variant="h2">Generate Metrics Report</Header>}>
        <SpaceBetween size="m">
          <SpaceBetween direction="horizontal" size="l">
            <div style={{ width: 250 }}>
              <Select
                selectedOption={PERIOD_OPTIONS.find((o) => o.value === period) || PERIOD_OPTIONS[1]}
                options={PERIOD_OPTIONS}
                onChange={({ detail }) => setPeriod(detail.selectedOption.value as PeriodType)}
              />
            </div>
            {period === "custom" && (
              <DateRangePicker
                value={dateRange}
                onChange={({ detail }) => setDateRange(detail.value)}
                relativeOptions={[]}
                isValidRange={() => ({ valid: true })}
                placeholder="Select date range"
                i18nStrings={{
                  todayAriaLabel: "Today",
                  nextMonthAriaLabel: "Next month",
                  previousMonthAriaLabel: "Previous month",
                  customRelativeRangeDurationLabel: "Duration",
                  customRelativeRangeDurationPlaceholder: "Enter duration",
                  customRelativeRangeOptionLabel: "Custom range",
                  customRelativeRangeOptionDescription: "Set a custom range",
                  customRelativeRangeUnitLabel: "Unit",
                  formatRelativeRange: () => "",
                  formatUnit: () => "",
                  dateTimeConstraintText: "",
                  relativeModeTitle: "Relative",
                  absoluteModeTitle: "Absolute",
                  relativeRangeSelectionHeading: "Choose a range",
                  startDateLabel: "Start date",
                  endDateLabel: "End date",
                  startTimeLabel: "Start time",
                  endTimeLabel: "End time",
                  clearButtonLabel: "Clear",
                  cancelButtonLabel: "Cancel",
                  applyButtonLabel: "Apply",
                }}
              />
            )}
          </SpaceBetween>
          <Checkbox
            checked={comparePrevious}
            onChange={({ detail }) => setComparePrevious(detail.checked)}
          >
            Compare to previous period
          </Checkbox>
          <Button variant="primary" onClick={handleGenerateReport}>
            Generate Report
          </Button>
        </SpaceBetween>
      </Container>

      {/* Generated report */}
      {reportGenerated && (
        <Container
          header={
            <Header
              variant="h2"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={handleCopyToClipboard} iconName="copy">
                    Copy to Clipboard
                  </Button>
                  <Button onClick={handleExportCsv} iconName="download">
                    Export CSV
                  </Button>
                </SpaceBetween>
              }
            >
              Generated Report
            </Header>
          }
        >
          <Textarea
            value={reportText}
            readOnly
            rows={Math.min(30, reportText.split("\n").length + 2)}
          />
        </Container>
      )}
    </SpaceBetween>
  );
}
